import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="tabs"
export default class extends Controller {
  static values = {
    activeTabIndex: Number,
    tabMap: Object,
  };

  static targets = ["tab"];

  connect() {
    // Get the tab group element (el-tab-group)
    this.tabGroup = this.element;

    // Store bound handler for cleanup
    this.boundHandlePopState = this.handlePopState.bind(this);

    // Listen for browser back/forward navigation
    window.addEventListener("popstate", this.boundHandlePopState);

    // Set initial active tab from server-rendered value or URL
    this.initializeActiveTab();
  }

  disconnect() {
    window.removeEventListener("popstate", this.boundHandlePopState);
  }

  // Called automatically when activeTabIndex value changes
  activeTabIndexValueChanged() {
    this.setActiveTab(this.activeTabIndexValue);
  }

  // Called automatically when tabMap value changes
  tabMapValueChanged() {
    // If we don't have an activeTabIndex set, initialize from URL
    if (!this.hasActiveTabIndexValue) {
      this.initializeActiveTab();
    }
  }

  // Called via Stimulus action from tab buttons
  switchTab(event) {
    const tabButton = event.currentTarget;
    const tabId = tabButton.dataset.tabId;

    if (tabId && this.tabMapValue && this.tabMapValue[tabId] !== undefined) {
      // Update URL without page reload
      this.updateURL(tabId);
      // The Tailwind Plus Elements library will handle the visual tab switching
    }
  }

  handlePopState(event) {
    // When browser back/forward is used, read the tab from URL
    const tabId = this.getTabIdFromURL();
    if (tabId && this.tabMapValue && this.tabMapValue[tabId] !== undefined) {
      this.setActiveTab(this.tabMapValue[tabId]);
    } else if (this.tabMapValue) {
      // Default to first tab if no valid tab in URL
      const firstTabIndex = Math.min(...Object.values(this.tabMapValue));
      this.setActiveTab(firstTabIndex);
    }
  }

  setActiveTab(index) {
    // Use the Tailwind Plus Elements API to set active tab
    if (this.tabGroup && typeof this.tabGroup.setActiveTab === "function") {
      this.tabGroup.setActiveTab(index);
    }
  }

  updateURL(tabId) {
    if (!this.tabMapValue) return;

    const url = new URL(window.location);
    url.searchParams.set("tab", tabId);

    // Update URL without page reload
    window.history.pushState({ tab: tabId }, "", url);
  }

  getTabIdFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("tab");
  }

  initializeActiveTab() {
    if (this.hasActiveTabIndexValue) {
      this.setActiveTab(this.activeTabIndexValue);
    } else {
      // Fallback: read from URL
      const tabId = this.getTabIdFromURL();
      if (tabId && this.tabMapValue && this.tabMapValue[tabId] !== undefined) {
        this.setActiveTab(this.tabMapValue[tabId]);
      }
    }
  }
}
